import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaDownload, FaCrown } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import { format, addDays, formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import { FOUNDER_EMAILS } from '../config';

const TokenBalance = ({ balance, backgroundImage, isImageLoading, isAuthenticated, onLogin, resetDate, monthlyAllowance, isFounder, monthlyUsage, isLoadingBalance, tokenStats }) => {
  // Use the monthly usage for all users (founders and subscribers)
  const actualMonthlyUsage = monthlyUsage;
  
  // Determine if user has an active subscription (monthly allowance > 0)
  const hasSubscription = monthlyAllowance > 0;
  
  // Get purchased and gifted token counts from tokenStats
  const purchasedTokensFromHistory = tokenStats?.totalPurchasedTokens || 0;
  const giftedTokensFromHistory = tokenStats?.totalGiftedTokens || 0;
  
  // Calculate token breakdown based on subscription status
  let monthlyTokensUsed, monthlyTokensLeft, purchasedTokens, giftedTokens, totalTokens, totalAvailableBeforeUsage;
  
  if (isFounder) {
    // Founders have unlimited tokens
    monthlyTokensUsed = actualMonthlyUsage;
    monthlyTokensLeft = 0;
    purchasedTokens = 0;
    giftedTokens = 0;
    totalTokens = Infinity;
    totalAvailableBeforeUsage = Infinity;
  } else if (hasSubscription) {
    // User has a subscription - they get monthly allowance + any purchased/gifted tokens
    monthlyTokensUsed = actualMonthlyUsage;
    monthlyTokensLeft = Math.max(0, monthlyAllowance - actualMonthlyUsage);
    
    // For subscribers, we need to figure out what portion of their usage came from where
    // Current balance = remaining tokens after usage
    // We assume monthly tokens are used first, then purchased/gifted
    const monthlyTokensConsumed = Math.min(actualMonthlyUsage, monthlyAllowance);
    const extraTokensUsed = Math.max(0, actualMonthlyUsage - monthlyAllowance);
    
    // Remaining purchased/gifted tokens = current balance - remaining monthly tokens
    const remainingPurchasedGifted = balance - monthlyTokensLeft;
    
    // Use historical data for total purchased/gifted, adjust for what's been used
    if (purchasedTokensFromHistory + giftedTokensFromHistory > 0) {
      const purchaseRatio = purchasedTokensFromHistory / (purchasedTokensFromHistory + giftedTokensFromHistory);
      const giftRatio = giftedTokensFromHistory / (purchasedTokensFromHistory + giftedTokensFromHistory);
      purchasedTokens = Math.max(0, purchasedTokensFromHistory - Math.floor(extraTokensUsed * purchaseRatio));
      giftedTokens = Math.max(0, giftedTokensFromHistory - Math.floor(extraTokensUsed * giftRatio));
    } else {
      purchasedTokens = 0;
      giftedTokens = 0;
    }
    
    // Total available now
    totalTokens = balance;
    // Total that was available before any usage this month
    totalAvailableBeforeUsage = monthlyAllowance + purchasedTokensFromHistory + giftedTokensFromHistory;
  } else {
    // Free user - balance contains purchased tokens and/or gifted tokens
    monthlyTokensUsed = actualMonthlyUsage; // This represents their total usage
    monthlyTokensLeft = 0;
    
    // For free users, calculate remaining based on what they started with vs current balance
    const totalHistoricalTokens = purchasedTokensFromHistory + giftedTokensFromHistory;
    const tokensUsed = Math.max(0, totalHistoricalTokens - balance);
    
    // Proportionally reduce purchased and gifted based on usage
    if (totalHistoricalTokens > 0) {
      const usageRatio = tokensUsed / totalHistoricalTokens;
      purchasedTokens = Math.max(0, Math.floor(purchasedTokensFromHistory * (1 - usageRatio)));
      giftedTokens = Math.max(0, Math.floor(giftedTokensFromHistory * (1 - usageRatio)));
    } else {
      purchasedTokens = 0;
      giftedTokens = 0;
    }
    
    totalTokens = balance;
    totalAvailableBeforeUsage = totalHistoricalTokens || balance;
  }
  
  // Calculate usage percentage based on total available tokens before usage
  const usagePercentage = totalAvailableBeforeUsage > 0 && totalAvailableBeforeUsage !== Infinity ? (monthlyTokensUsed / totalAvailableBeforeUsage) * 100 : 0;
  
  return (
    <motion.div
      className="relative overflow-hidden rounded-xl"
      whileHover={{ scale: 1.02 }}
    >
      {/* Background Image with Gradient */}
      <div className="absolute inset-0">
        {backgroundImage ? (
          <img 
            src={backgroundImage} 
            alt="" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-black/40 to-black/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/20" />
      </div>

      {/* Content */}
      <div className="relative p-8 backdrop-blur-sm">
        {isFounder && (
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl text-white/80 font-['VT323']">👑 FOUNDER STATUS</h2>
            {monthlyUsage > 0 && (
              <div className="flex items-center gap-2">
                <FaCrown className="text-yellow-500" />
                <span className="font-['VT323'] text-white/60">
                  {monthlyUsage} generated this month
                </span>
              </div>
            )}
          </div>
        )}

        {!isFounder && (
          <div className="text-5xl font-['VT323'] text-white mb-8">
            {isAuthenticated ? (
              isLoadingBalance ? (
                <span className="text-white/60">LOADING...</span>
              ) : (
                balance === Infinity ? '∞ TOKENS' : `YOU HAVE ${balance} TOKEN${balance !== 1 ? 'S' : ''}`
              )
            ) : (
              <button 
                onClick={onLogin}
                className="text-white/60 hover:text-white transition-colors"
              >
                Login to View Balance
              </button>
            )}
          </div>
        )}





        {/* Token Usage Bar - Only show for non-founders */}
        {isAuthenticated && balance !== Infinity && !isFounder && (totalAvailableBeforeUsage > 0 && totalAvailableBeforeUsage !== Infinity) && (
          <div className="space-y-4">
            {/* Full-width Token Bar Graph */}
            <div className="h-8 bg-white/10 rounded-lg overflow-hidden flex w-full">
              {/* Used Tokens (Red) - only show if usage > 0 */}
              {monthlyTokensUsed > 0 && (
                <motion.div 
                  className="h-full bg-red-400/70"
                  initial={{ width: 0 }}
                  animate={{ width: `${(monthlyTokensUsed / totalAvailableBeforeUsage) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              )}
              {/* Remaining Monthly Tokens (White) - only for subscribers */}
              {hasSubscription && monthlyTokensLeft > 0 && (
                <motion.div 
                  className="h-full bg-white/50"
                  initial={{ width: 0 }}
                  animate={{ width: `${(monthlyTokensLeft / totalAvailableBeforeUsage) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              )}
              {/* Remaining Purchased Tokens (Green) */}
              {purchasedTokens > 0 && (
                <motion.div 
                  className="h-full bg-[#00FF9F]/70"
                  initial={{ width: 0 }}
                  animate={{ width: `${(purchasedTokens / totalAvailableBeforeUsage) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              )}
              {/* Remaining Gifted Tokens (Purple) */}
              {giftedTokens > 0 && (
                <motion.div 
                  className="h-full bg-purple-400/70"
                  initial={{ width: 0 }}
                  animate={{ width: `${(giftedTokens / totalAvailableBeforeUsage) * 100}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              )}
            </div>

            {/* Clean Legend */}
            <div className="flex flex-wrap gap-4 text-sm font-['VT323'] text-white/60">
              {/* Always show monthly usage for regular users */}
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${monthlyTokensUsed > 0 ? 'bg-red-400/70' : 'bg-white/20'}`} />
                <span>{monthlyTokensUsed} used this month</span>
              </div>
              {hasSubscription && monthlyTokensLeft > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-white/50 rounded" />
                  <span>{monthlyTokensLeft} monthly remaining</span>
                </div>
              )}
              {purchasedTokens > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#00FF9F]/70 rounded" />
                  <span>{purchasedTokens} purchased left</span>
                </div>
              )}
              {giftedTokens > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-400/70 rounded" />
                  <span>{giftedTokens} gifted left</span>
                </div>
              )}
            </div>

            {/* Simple plan indicator */}
            {!hasSubscription && (
              <div className="text-sm font-['VT323'] text-white/50">
                🆓 Free plan - Subscribe to get monthly allowances
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const BillingHistory = ({ isAuthenticated, onLogin, isFounder = false }) => {
  const [activities, setActivities] = useState([]);
  const [tokenStats, setTokenStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearingPurchases, setIsClearingPurchases] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  // Only show clear button in development
  const isDevelopment = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'development';

  const clearPurchases = async () => {
    if (!isAuthenticated) return;

    const confirmed = window.confirm(
      '🧹 This will clear all your purchase history and reset your token balance to 0. This is for testing only. Continue?'
    );

    if (!confirmed) return;

    try {
      setIsClearingPurchases(true);
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tokens/purchases/clear`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resetTokenBalance: true,
          newTokenBalance: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to clear purchases');
      }

      const result = await response.json();
      
      
      
      // Clear the local activities state
      setActivities([]);
      setTokenStats(null);
      
      toast.success(`🎉 Cleared ${result.deletedPurchases} purchases! Balance reset to ${result.newTokenBalance} tokens.`, {
        duration: 5000
      });

      // Refresh the page to update all token displays
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('[RETRO] ❌ Error clearing purchases:', error);
      toast.error('Failed to clear purchases. Check console for details.');
    } finally {
      setIsClearingPurchases(false);
    }
  };

  useEffect(() => {
    const fetchTokenActivities = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          }
        });

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tokens/activities`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch token activities');
        }

        const data = await response.json();
        setActivities(data.activities || []);
        setTokenStats(data.stats || null);
      } catch (error) {
        console.error('[RETRO] ❌ Error fetching token activities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTokenActivities();
  }, [getAccessTokenSilently, isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="text-center py-8 text-black/50 font-['VT323']">
        Please <button 
          onClick={onLogin}
          className="text-blue-500 hover:underline"
        >
          log in
        </button> to view your billing history
      </div>
    );
  }

  // Group activities by month
  const activitiesByMonth = activities.reduce((acc, activity) => {
    const monthYear = format(new Date(activity.date), 'MMMM yyyy');
    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(activity);
    return acc;
  }, {});

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'succeeded':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-black/50';
    }
  };

  const getTypeLabel = (activity) => {
    const { type, amount, isPurchase, isGift, reason } = activity;
    
    if (isPurchase || type === 'TOKEN_PURCHASE' || type === 'PAYMENT') {
      return amount ? `Token Purchase - ${amount} Tokens` : 'Token Purchase';
    } else if (isGift || type === 'gift') {
      return amount ? `Token Gift - ${amount} Tokens` : 'Token Gift';
    } else if (type === 'download_all') {
      return 'Download All Images';
    } else {
      return amount ? `${type.charAt(0).toUpperCase() + type.slice(1)} - ${amount} Tokens` : type;
    }
  };

  const getActivityIcon = (activity) => {
    if (activity.isPurchase) return '💳';
    if (activity.isGift) return '🎁';
    return '📋';
  };

      return (
      <div>
        {isLoading ? (
          <div className="text-center py-8 text-black/50 font-['VT323']">
            Loading token history...
          </div>
        ) : Object.entries(activitiesByMonth).length === 0 ? (
          <div className="text-center py-8 text-black/50 font-['VT323']">
            No token activities yet. Buy some tokens or receive gifts to get started!
          </div>
        ) : (
          Object.entries(activitiesByMonth).map(([monthYear, monthActivities]) => (
            <div key={monthYear} className="bg-black/[0.03] rounded-xl overflow-hidden mb-6">
              <div className="flex items-center justify-between p-4 bg-black/[0.02]">
                <div className="font-['VT323'] text-lg text-black/70">📅 {monthYear.toUpperCase()}</div>
                <div className="font-['VT323'] text-black/50">
                  {monthActivities.length} activit{monthActivities.length !== 1 ? 'ies' : 'y'}
                </div>
              </div>
              
              <div className="p-4 space-y-4">
                {monthActivities.map((activity, index) => (
                  <div 
                    key={activity.id} 
                    className={`${index !== monthActivities.length - 1 ? 'border-b border-black/[0.06]' : ''} pb-4`}
                  >
                    <div className="flex justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span>{getActivityIcon(activity)}</span>
                        <div className="font-['VT323'] text-black/70">
                          {format(new Date(activity.date), 'MMM dd').toUpperCase()}
                        </div>
                        {activity.isGift && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-['VT323'] rounded">
                            GIFT
                          </span>
                        )}
                        {activity.isPurchase && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-['VT323'] rounded">
                            PURCHASE
                          </span>
                        )}
                      </div>
                      <div className="font-['VT323'] text-black/70">
                        {activity.isPurchase ? `$${activity.cost?.toFixed(2) || '0.00'}` : 'FREE'}
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="font-['VT323'] text-black/50">
                        {getTypeLabel(activity)}
                      </div>
                      <div className={`font-['VT323'] ${getStatusColor(activity.status)}`}>
                        {activity.status?.toUpperCase() || 'COMPLETED'}
                      </div>
                    </div>
                    {activity.reason && activity.isGift && (
                      <div className="text-xs font-['VT323'] text-black/40 mt-1">
                        {activity.reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
};

const TokenManager = ({ tokenBalance, onTokenBalanceChange, resetDate, monthlyAllowance, isFounder, monthlyUsage, isLoadingBalance }) => {
  const [founderImages, setFounderImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [tokenStats, setTokenStats] = useState(null);
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchFounderImages = async () => {
      try {
        setIsLoadingImages(true);
        
        // Try each founder in sequence until we get images
        for (const founder of FOUNDER_EMAILS) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/favorites/public/founder?founder=${encodeURIComponent(founder)}`);
            
            if (!response.ok) {
              console.warn(`Failed to fetch images for founder ${founder}`);
              continue;
            }
            
            const data = await response.json();
            if (data && data.length > 0) {
              // Shuffle the images and take a few for backgrounds
              const shuffled = [...data].sort(() => Math.random() - 0.5);
              setFounderImages(shuffled.slice(0, 4).map(img => ({
                ...img,
                url: img.url || img.s3Url || img.thumbnailUrl // Try all possible URL fields
              })));
              break; // Exit the loop once we have images
            }
          } catch (error) {
            console.warn(`Error fetching images for founder ${founder}:`, error);
          }
        }
      } catch (error) {
        console.error('[RETRO] ❌ Error fetching founder images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    fetchFounderImages();
  }, []);

  useEffect(() => {
    const fetchTokenStats = async () => {
      if (!isAuthenticated) {
        return;
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          }
        });

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tokens/activities`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTokenStats(data.stats || null);
        }
      } catch (error) {
        console.error('[RETRO] ❌ Error fetching token stats:', error);
      }
    };

    fetchTokenStats();
  }, [isAuthenticated, getAccessTokenSilently]);

  return (
    <TokenBalance 
      balance={tokenBalance} 
      backgroundImage={founderImages[0]?.url}
      isImageLoading={isLoadingImages}
      isAuthenticated={isAuthenticated}
      onLogin={loginWithRedirect}
      resetDate={resetDate}
      monthlyAllowance={monthlyAllowance}
      isFounder={isFounder}
      monthlyUsage={monthlyUsage}
      isLoadingBalance={isLoadingBalance}
      tokenStats={tokenStats}
    />
  );
}

export { BillingHistory };
export default TokenManager; 